class MarqueeScroller {
    constructor(section) {
      this.section = section;
      this.viewport = section.querySelector('.marquee__viewport');
      this.inner = section.querySelector('.marquee__inner');
  
      if (!this.viewport || !this.inner) return;
  
      this.parallaxEnabled = section.dataset.marqueeParallax === 'true';
      this.parallaxStrength = parseFloat(section.dataset.marqueeParallaxStrength || 0.2) || 0;
      this.pauseEnabled = !this.parallaxEnabled && section.dataset.marqueePause === 'true';
      this.duration = Number(section.dataset.marqueeSpeed) || 20;
      this.direction = section.dataset.marqueeDirection === 'reverse' ? 'reverse' : 'normal';
      this.pauseDuration = 0.5;
      this.directionFactor = this.direction === 'reverse' ? 1 : -1;
  
      this.animation = null;
      this.distance = 0;
      this.pausedTime = null;
      this.isPaused = false;
      this.isPausing = false;
      this.playbackFrame = null;
      this.pendingRateFrame = null;
      this.parallaxFrame = null;
      this.lastScrollPosition = window.scrollY || window.pageYOffset || 0;
      this.lastViewportWidth = this.viewport ? Math.round(this.viewport.getBoundingClientRect().width) : null;
  
      this.originalMarkup = this.inner.innerHTML;
      this.onResize = this.handleResize.bind(this);
      this.onScroll = this.handleScroll.bind(this);
  
      this.populate();
      window.addEventListener('resize', this.onResize);
      this.initialized = true;
  
      if (this.parallaxEnabled) {
        this.startParallax();
      } else {
        requestAnimationFrame(() => this.ensureAnimation());
      }
  
      if (this.pauseEnabled) {
        this.pointerEnterHandler = this.onPointerEnter.bind(this);
        this.pointerLeaveHandler = this.onPointerLeave.bind(this);
        section.addEventListener('mouseenter', this.pointerEnterHandler);
        section.addEventListener('mouseleave', this.pointerLeaveHandler);
        section.addEventListener('focusin', this.pointerEnterHandler);
        section.addEventListener('focusout', this.pointerLeaveHandler);
      }
    }
  
    populate() {
      if (!this.inner) return;
      this.inner.style.animation = 'none';
      this.inner.style.transform = 'translate3d(0, 0, 0)';
      this.inner.innerHTML = this.originalMarkup;
  
      let groups = Array.from(this.inner.querySelectorAll('.marquee__group'));
      if (!groups.length) {
        requestAnimationFrame(() => {
          this.inner.style.animation = '';
        });
        return;
      }
  
      const baseGroup = this.inner.querySelector('.marquee__group');
      if (!baseGroup || !this.viewport) {
        requestAnimationFrame(() => {
          this.inner.style.animation = '';
        });
        return;
      }
  
      const viewportWidth = this.viewport.getBoundingClientRect().width;
      this.lastViewportWidth = Math.round(viewportWidth);
      let baseWidth = baseGroup.getBoundingClientRect().width;
  
      if (baseWidth === 0) {
        requestAnimationFrame(() => {
          this.inner.style.animation = '';
        });
        return;
      }
  
      const targetWidth = viewportWidth + baseWidth;
      while (this.inner.scrollWidth < targetWidth) {
        const clone = baseGroup.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
        this.inner.appendChild(clone);
      }
  
      groups = Array.from(this.inner.querySelectorAll('.marquee__group'));
      groups.forEach((group, index) => {
        if (index === 0) {
          group.removeAttribute('aria-hidden');
        } else {
          group.setAttribute('aria-hidden', 'true');
        }
      });
  
      baseWidth = baseGroup.getBoundingClientRect().width || baseWidth;
      this.distance = baseWidth;
      this.section.style.setProperty('--marquee-distance', `${baseWidth}px`);
  
      this.animation = null;
      this.pausedTime = null;
      this.isPaused = false;
      this.isPausing = false;
      if (this.playbackFrame) {
        cancelAnimationFrame(this.playbackFrame);
        this.playbackFrame = null;
      }
      if (this.pendingRateFrame) {
        cancelAnimationFrame(this.pendingRateFrame);
        this.pendingRateFrame = null;
      }
      if (this.parallaxFrame) {
        cancelAnimationFrame(this.parallaxFrame);
        this.parallaxFrame = null;
      }
  
      void this.inner.offsetWidth;
      if (this.parallaxEnabled) {
        this.inner.style.animation = 'none';
        this.updateParallax(this.lastScrollPosition);
      } else {
        this.inner.style.animation = '';
        requestAnimationFrame(() => this.ensureAnimation());
      }
    }
  
    startParallax() {
      this.updateParallax(this.lastScrollPosition);
      window.addEventListener('scroll', this.onScroll, { passive: true });
    }
  
    ensureAnimation() {
      if (this.parallaxEnabled) return null;
      if (!this.inner) return null;
      const animations = this.inner.getAnimations();
      if (!animations.length) {
        this.animation = null;
        return null;
      }
      this.animation = animations[0];
      if (this.pausedTime != null && this.isPaused) {
        this.animation.currentTime = this.pausedTime;
        this.animation.pause();
      }
      return this.animation;
    }
  
    easeToRate(targetRate) {
      if (this.parallaxEnabled) return;
      const animation = this.ensureAnimation();
      if (!animation) {
        if (!this.pendingRateFrame) {
          this.pendingRateFrame = requestAnimationFrame(() => {
            this.pendingRateFrame = null;
            this.easeToRate(targetRate);
          });
        }
        return;
      }
  
      if (targetRate === 0 && this.isPaused) return;
      if (targetRate === 1 && !this.isPaused && !this.isPausing) return;
  
      if (this.playbackFrame) {
        cancelAnimationFrame(this.playbackFrame);
        this.playbackFrame = null;
      }
  
      const durationMs = this.pauseDuration * 1000;
      const startTime = performance.now();
      let startRate = animation.playbackRate || 1;
  
      if (animation.playState === 'paused' && targetRate > 0) {
        if (this.pausedTime != null) {
          animation.currentTime = this.pausedTime;
        }
        animation.play();
        startRate = 0;
        animation.playbackRate = 0;
      } else {
        animation.play();
      }
  
      const easing =
        targetRate < startRate
          ? (t) => 1 - Math.pow(1 - t, 2) // ease-out
          : (t) => t * t; // ease-in
  
      this.isPausing = targetRate === 0;
  
      const step = (now) => {
        const t = Math.min(1, (now - startTime) / durationMs);
        const eased = easing(t);
        const rate = startRate + (targetRate - startRate) * eased;
        animation.playbackRate = Math.max(rate, 0.0001);
  
        if (t < 1) {
          this.playbackFrame = requestAnimationFrame(step);
        } else {
          this.playbackFrame = null;
          if (targetRate === 0) {
            animation.pause();
            animation.playbackRate = 1;
            this.pausedTime = animation.currentTime;
            this.isPaused = true;
          } else {
            animation.playbackRate = 1;
            animation.play();
            this.pausedTime = null;
            this.isPaused = false;
          }
          this.isPausing = false;
        }
      };
  
      this.playbackFrame = requestAnimationFrame(step);
    }
  
    onPointerEnter(event) {
      if (this.parallaxEnabled) return;
      if (!this.pauseEnabled) return;
      if (event.type === 'focusin' && !this.section.contains(event.target)) return;
      this.easeToRate(0);
    }
  
    onPointerLeave(event) {
      if (this.parallaxEnabled) return;
      if (!this.pauseEnabled) return;
  
      if (event.type === 'focusout') {
        if (event.relatedTarget && this.section.contains(event.relatedTarget)) return;
      }
  
      if (event.type === 'mouseleave') {
        if (event.relatedTarget && this.section.contains(event.relatedTarget)) return;
      }
  
      if (!this.isPaused && !this.isPausing) return;
      this.easeToRate(1);
    }
  
    handleResize() {
      if (!this.viewport) return;
      const currentWidth = Math.round(this.viewport.getBoundingClientRect().width);
      const widthChanged = !this.lastViewportWidth || Math.abs(currentWidth - this.lastViewportWidth) > 1;
      if (!widthChanged) {
        if (this.parallaxEnabled) {
          this.lastScrollPosition = window.scrollY || window.pageYOffset || 0;
          this.updateParallax(this.lastScrollPosition);
        }
        return;
      }
      this.lastViewportWidth = currentWidth;
  
      if (this.parallaxEnabled) {
        if (this.parallaxFrame) {
          cancelAnimationFrame(this.parallaxFrame);
          this.parallaxFrame = null;
        }
        this.lastScrollPosition = window.scrollY || window.pageYOffset || 0;
      }
  
      if (this.playbackFrame) {
        cancelAnimationFrame(this.playbackFrame);
        this.playbackFrame = null;
      }
      if (this.pendingRateFrame) {
        cancelAnimationFrame(this.pendingRateFrame);
        this.pendingRateFrame = null;
      }
      this.pausedTime = null;
      this.isPaused = false;
      this.isPausing = false;
      this.populate();
    }
  
    handleScroll() {
      if (!this.parallaxEnabled) return;
      this.lastScrollPosition = window.scrollY || window.pageYOffset || 0;
      if (this.parallaxFrame) return;
      this.parallaxFrame = requestAnimationFrame(() => {
        this.parallaxFrame = null;
        this.updateParallax(this.lastScrollPosition);
      });
    }
  
    updateParallax(scrollPosition) {
      if (!this.parallaxEnabled || !this.inner) return;
      if (!this.distance || !Number.isFinite(this.distance)) {
        this.inner.style.transform = 'translate3d(0, 0, 0)';
        return;
      }
  
      const strength = this.parallaxStrength;
      const movement = scrollPosition * strength * this.directionFactor;
      let offset = movement % this.distance;
  
      if (offset > 0) {
        offset -= this.distance;
      }
  
      this.inner.style.transform = `translate3d(${offset}px, 0, 0)`;
    }
  
    destroy() {
      if (this.parallaxEnabled) {
        window.removeEventListener('scroll', this.onScroll);
        if (this.parallaxFrame) {
          cancelAnimationFrame(this.parallaxFrame);
        }
      }
  
      if (this.pauseEnabled) {
        this.section.removeEventListener('mouseenter', this.pointerEnterHandler);
        this.section.removeEventListener('mouseleave', this.pointerLeaveHandler);
        this.section.removeEventListener('focusin', this.pointerEnterHandler);
        this.section.removeEventListener('focusout', this.pointerLeaveHandler);
      }
  
      if (this.playbackFrame) {
        cancelAnimationFrame(this.playbackFrame);
      }
      if (this.pendingRateFrame) {
        cancelAnimationFrame(this.pendingRateFrame);
      }
      if (this.parallaxFrame) {
        cancelAnimationFrame(this.parallaxFrame);
      }
  
      window.removeEventListener('resize', this.onResize);
  
      if (this.inner) {
        this.inner.innerHTML = this.originalMarkup;
        this.inner.style.animation = '';
      }
  
      this.section.style.removeProperty('--marquee-distance');
      this.animation = null;
      this.lastViewportWidth = null;
    }
  }
  
  const initMarquees = (root = document) => {
    root.querySelectorAll('.marquee-section').forEach((section) => {
      if (section.__marqueeScrollerInstance) {
        section.__marqueeScrollerInstance.populate();
        return;
      }
      const instance = new MarqueeScroller(section);
      if (instance.initialized) {
        section.__marqueeScrollerInstance = instance;
      }
    });
  };
  
  document.addEventListener('DOMContentLoaded', () => {
    initMarquees();
  });
  
  document.addEventListener('shopify:section:load', (event) => {
    initMarquees(event.target);
  });
  
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('.marquee-section').forEach((section) => {
      if (section.__marqueeScrollerInstance) {
        section.__marqueeScrollerInstance.destroy();
        delete section.__marqueeScrollerInstance;
      }
    });
  });
  
  document.addEventListener('shopify:block:select', (event) => {
    const marqueeSection = event.target.closest('.marquee-section');
    if (!marqueeSection || !marqueeSection.__marqueeScrollerInstance) return;
    marqueeSection.__marqueeScrollerInstance.populate();
  });
  